import { useMemo } from 'react';
import useProposals from './useProposals';
import useWorkOrders from './useWorkOrders';
import useInvoices from './useInvoices';
import useUsers from './useUsers';
import useCommissions from './useCommissions';
import { ProposalStatus } from '../models/proposal';
import { WorkOrderStatus } from '../models/workOrder';
import { InvoiceStatus } from '../models/invoice';
import { CommissionEntryType, commissionTypeLabel } from '../models/commission';
import { isArchivedRow } from '../utils/archived';

// item 177 — the "Sales Bible": one row per job, the auto-populated half of the customer's
// tracking sheet. item 607 — covers EVERY job type: proposal-based + standalone WO/service jobs.
// item 623 (Chad audit) — the status model now MIRRORS the real lifecycle (was the invented
// Opportunity/Sold vocabulary). Five clean states + a hierarchy that flows top→bottom:
//   In Progress → Pending → Completed → Declined → Voided.
export type SalesState = 'In Progress' | 'Pending' | 'Completed' | 'Declined' | 'Voided';

// item 623 — the sort weight for each state (lower = higher on the board). "Active-first"
// (Chad's pick): work needing execution at top, live opportunities next, done below, dead last.
export const STATE_BUCKET: Record<SalesState, number> = {
  'In Progress': 0,
  Pending: 1,
  Completed: 2,
  Declined: 3,
  Voided: 4,
};

// item 625 — plain-language meaning of each state, for the status hover on the board (and
// reusable by any future status legend). Definitions mirror mapProposalState/accountingDone.
export const STATE_MEANING: Record<SalesState, string> = {
  'In Progress': 'Won and being executed — not yet billed all the way through ($0 left to '
    + 'bill with every invoice finalized).',
  Pending: 'A live opportunity — the proposal is out and not yet approved.',
  // item 1258 (Chad: "take it complete when $0 is left to bill — Luxor is 100% complete,
  // they just owe the invoice") — payment no longer gates Completed; collection state
  // rides the PAYMENT column instead.
  Completed: 'Done — either marked Complete, or billed all the way through ($0 left to bill, '
    + 'every invoice finalized). Whether the money is in yet shows in the PAYMENT column.',
  Declined: 'The customer declined this proposal.',
  Voided: 'This job was voided.',
};

// item 606 — the assigned earners for one commission reason on a job (small grouped text).
export interface CommissionEarnerGroup {
  reason: string;
  names: string[];
}

// item 1144 — one graded rule on the job's lifecycle trail
export interface LifecycleStep {
  label: string;
  done: boolean;
  detail?: string;
  href?: string;
}

export interface SalesBibleRow {
  id: string;
  // item 607 — a row is either a proposal-based job or a standalone work-order job; the
  // click-through opens the right document, and the manual tracking columns only persist
  // for proposal rows (the SalesBible endpoint is keyed to a proposal).
  kind: 'proposal' | 'workOrder';
  jobName: string;
  projectName: string; // item 1587 — the official project the job comes from
  state: SalesState;
  bucket: number; // item 608 — STATE_BUCKET[state], the default sort weight
  completed: boolean; // a Complete proposal = a "checked-off" sold job
  integrator: string;
  customerStaffPm: string; // item 612 — the customer's PM / staff contact
  salesPerson: string;
  projectLead: string; // the job's lead = the Work Order's ProjectManager (set at approval)
  // item 606 — the assigned commission earners grouped by reason (who's getting paid)
  commissionEarners: CommissionEarnerGroup[];
  contracted: boolean;
  depositBilled: boolean;
  finalBilled: boolean;
  proposalNumber: string;
  poNumber: string; // item 611 — the customer's real PO number (captured at approval); '—' when unset
  workOrderNumber: string; // item 609 — the job code (WO#), '' until a WO exists
  workOrderId?: string; // item 1171 — deep link for the Assign→ cells (lead lives on the WO)
  customerId?: string; // item 1172 — the staff roster source for the assign-PM modal
  // item 1458 (Dustin via Chad) — deep links off the board, all new-tab: the job's PROJECT
  // (integrator cell), the customer PM's STAFF row (customer page anchor), and the
  // deposit / final INVOICES behind the billed flags.
  projectId?: string;
  customerPmId?: string; // CustomerStaff.id of the assigned customer PM
  depositInvoiceId?: string; // first live invoice on the job
  finalInvoiceId?: string; // last live invoice once the WO is fully invoiced
  // item 1460 — an approved-era, non-void PROPOSAL has a hand-off (the numbers-free deployment
  // brief, items 423/461): the job name goes to the project's HAND-OFFS section + an eye previews it
  hasHandOff: boolean;
  // item 285 — dates for fiscal-year scoping: `date` = the proposal/WO date (which FY the
  // job belongs to); `closedDate` = last-modified (proxy for when it went Declined/Completed).
  date: string;
  closedDate: string;
  // item 177 (v2) — manual, editable tracking fields (saved via the SalesBible endpoint;
  // proposal rows only — WO rows render them read-only)
  platform: string;
  deploymentDate: string; // yyyy-MM-dd for the date input ('' when unset)
  illustrationsComplete: string; // '' | 'Yes' | 'No' | 'N/A'
  projectLeadPayoutInput: string;
  // item 1144 (Chad) — the WHY behind the state: the guided rules graded green/red
  lifecycle: LifecycleStep[];
  verdict: string; // '' when nothing is missing
  // item 1258 — the PAYMENT column: collection state, decoupled from the job's status
  paymentState: PaymentState;
  owedTotal: number;
  anyPaid: boolean; // item 1261 — drives the worded label (Deposited vs Open pre-final-bill)
  // item 1584 (Chad: written-off jobs "should have a list … the work was performed but we chose
  // not to bill") — what was written off across the job's live invoices. A fully written-off
  // invoice nets to $0, so without this it read PAID IN FULL.
  writtenOffTotal: number;

  notes: string;
}

// item 1258 (Chad, supersedes item 623's "accounting fully done" rule) — Completed = $0 LEFT
// TO BILL: every live invoice exists and is FINALIZED. Payment/entered/deposited no longer
// gate the job's status — the job is done when it's fully billed; whether the customer has
// paid rides the PAYMENT column ("Luxor is 100% complete, they just owe the invoice").
const billedThrough = (jobInvoices: { isVoid: boolean; isFinalized: boolean }[]): boolean => {
  const live = jobInvoices.filter((i) => !i.isVoid);
  if (live.length === 0) return false;
  return live.every((i) => i.isFinalized);
};

// item 1258/1261 — the PAYMENT column's facts, worded not numbered (Chad: "not numbers").
// The label follows the billing journey: while the job is still being billed, a paid invoice
// reads DEPOSITED; once finally invoiced, collection is PROGRESS until it's PAID IN FULL.
export type PaymentState = 'paid' | 'owes' | 'none';
const invoicePaid = (i: any): boolean => i.status === InvoiceStatus.Paid
  || i.status === InvoiceStatus.PaidEntered
  || (i.stripePaymentStatus ?? 0) !== 0 || !!i.paidDate;
const paymentFacts = (
  jobInvoices: any[],
): { paymentState: PaymentState; owedTotal: number; anyPaid: boolean; writtenOffTotal: number } => {
  const live = jobInvoices.filter((i) => !i.isVoid && i.isFinalized);
  if (live.length === 0) {
    return {
      paymentState: 'none', owedTotal: 0, anyPaid: false, writtenOffTotal: 0,
    };
  }
  const owed = live.filter((i) => !invoicePaid(i) && i.status !== InvoiceStatus.WrittenOff)
    .reduce((s, i) => s + (i.retailTotal ?? 0), 0);
  return {
    paymentState: owed > 0 ? 'owes' : 'paid',
    owedTotal: owed,
    anyPaid: live.some(invoicePaid),
    writtenOffTotal: live.reduce((s, i) => s + (i.writeOffAmount ?? 0), 0),
  };
};

// item 1458 — the invoices behind DEPOSIT BILLED / FINAL BILLED: live (non-void) invoices in
// date order; deposit = the first, final = the last once the job is fully invoiced (on a
// single-bill job both point at the one invoice).
const billedInvoiceIds = (
  jobInvoices: any[],
  finalBilled: boolean,
): { depositInvoiceId?: string; finalInvoiceId?: string } => {
  const live = jobInvoices.filter((i) => !i.isVoid)
    .slice()
    .sort((a, b) => String(a.date ?? '').localeCompare(String(b.date ?? '')));
  if (live.length === 0) return {};
  return {
    depositInvoiceId: live[0].id,
    finalInvoiceId: finalBilled ? live[live.length - 1].id : undefined,
  };
};

// item 1262 (Chad: "missing po" — GVR's PO lived on the INVOICE via item 733, not the
// proposal) — the PO # column reads the whole chain: the proposal's approval-captured PO
// first, else the distinct client PO numbers off the job's live invoices.
const poOf = (proposalPo: string | null | undefined, jobInvoices: any[]): string => {
  const p = (proposalPo ?? '').trim();
  if (p) return p;
  const fromInvoices = Array.from(new Set(
    jobInvoices.filter((i) => !i.isVoid)
      .map((i) => (i.customerPoNumber ?? '').trim())
      .filter((n: string) => n !== ''),
  ));
  return fromInvoices.length > 0 ? fromInvoices.join(' · ') : '—';
};

// item 1261 — the worded label + its sort rank (money-chasing first)
export type PaymentLabel = 'Paid in Full' | 'Progress' | 'Deposited' | 'Open' | 'Written Off' | '—';
export const paymentLabelOf = (r: {
  paymentState: PaymentState; anyPaid: boolean; finalBilled: boolean; writtenOffTotal?: number;
}): PaymentLabel => {
  if (r.paymentState === 'none') return '—';
  // item 1584 — nothing collected and nothing owed because it was written off
  if (r.paymentState === 'paid' && !r.anyPaid && (r.writtenOffTotal ?? 0) > 0) return 'Written Off';
  if (r.finalBilled) return r.paymentState === 'paid' ? 'Paid in Full' : 'Progress';
  return r.anyPaid ? 'Deposited' : 'Open';
};
export const PAYMENT_RANK: Record<PaymentLabel, number> = {
  Progress: 0, Open: 1, Deposited: 2, 'Paid in Full': 3, 'Written Off': 4, '—': 5,
};

// item 623 — PROPOSAL-based job → one of the five clean states. Void wins (surfaced now, no
// longer hidden); then the lifecycle: Declined; Pending (a live opportunity, not yet approved);
// Complete (the terminal "job is done" lifecycle status) → Completed outright; Approved/
// ApprovedDeposited → Completed once the money is fully processed (billed through + finalized +
// entered + deposited), else In Progress. So Completed = manually-marked Complete OR accounting
// fully done (Chad 2026-07-10 — the Complete status is itself a legitimate "done" signal).
const mapProposalState = (
  s: ProposalStatus,
  isVoid: boolean,
  finalBilled: boolean,
  jobInvoices: { isVoid: boolean; isFinalized: boolean }[],
): SalesState => {
  if (isVoid) return 'Voided';
  switch (s) {
    case ProposalStatus.Declined: return 'Declined';
    case ProposalStatus.Pending: return 'Pending';
    case ProposalStatus.Complete: return 'Completed';
    case ProposalStatus.Approved:
    case ProposalStatus.ApprovedDeposited:
    default:
      // item 1258 — Completed at $0 left to bill (WO fully invoiced + every invoice finalized)
      return finalBilled && billedThrough(jobInvoices) ? 'Completed' : 'In Progress';
  }
};

// item 1144 (Chad: "why is it still in progress, without green and red?") — grade the
// item-623 rules for ONE job: proposal approved → WO created → each invoice generated/
// finalized/sent/paid/entered+deposited → WO fully invoiced. The popup and the STATUS
// column read the same facts, so they can never disagree.
// item 1156b (Chad) — SENT is informational, never a requirement: invoices are often
// downloaded as PDFs and emailed outside Basix.
// item 1258 (Chad) — payment/entered/deposited are ALSO informational now: an invoice's step
// is DONE when it's finalized ($0 left to bill = job complete); collection state rides the
// step's detail text and the PAYMENT column, never the gate.
const invoiceStage = (i: any): { done: boolean; next: string; paid: boolean } => {
  const paid = invoicePaid(i);
  if (!i.isFinalized) return { done: false, next: 'finalize', paid };
  return { done: true, next: '', paid };
};

const buildLifecycle = (
  proposalStatus: ProposalStatus | null,
  wo: any,
  jobInvoices: any[],
  markedComplete: boolean,
): { lifecycle: LifecycleStep[]; verdict: string } => {
  const steps: LifecycleStep[] = [];
  if (proposalStatus != null) {
    const approved = proposalStatus === ProposalStatus.Approved
      || proposalStatus === ProposalStatus.ApprovedDeposited
      || proposalStatus === ProposalStatus.Complete;
    steps.push({ label: 'Proposal approved', done: approved });
  }
  steps.push({
    label: 'Work order created',
    done: !!wo,
    detail: wo?.workOrderNumber ?? undefined,
    href: wo ? `/WorkOrder/${wo.id}` : undefined,
  });
  const live = jobInvoices.filter((i) => !i.isVoid)
    .slice()
    .sort((a, b) => String(a.date ?? '').localeCompare(String(b.date ?? '')));
  if (live.length === 0) {
    steps.push({ label: 'Deposit invoice generated', done: false, detail: 'no invoice yet' });
  }
  live.forEach((i, idx) => {
    let name = 'Invoice';
    if (live.length > 1) name = idx === 0 ? 'Deposit invoice' : 'Final invoice';
    const stage = invoiceStage(i);
    // item 1258 — payment shown, never gating
    let detail = `next: ${stage.next}`;
    if (stage.done) detail = stage.paid ? 'finalized · paid' : 'finalized · payment open';
    steps.push({
      label: `${name} ${i.invoiceNumber ?? ''}`.trim(),
      done: stage.done,
      detail,
      href: `/invoice/${i.id}`,
    });
  });
  const woLocked = wo?.status === WorkOrderStatus.InvoicesGenerated;
  if (wo && !woLocked && live.length > 0) {
    steps.push({ label: 'Final invoice generated', done: false, detail: 'the work order is not fully invoiced yet' });
  }
  const firstMiss = steps.find((st) => !st.done);
  // item 1156 (Chad) — a job MARKED Complete can still carry open money (an invoice never
  // sent/paid through Basix). Don't show silent red ✗s: say it out loud so uncollected
  // cash on "done" jobs can't hide.
  if (markedComplete) {
    const open = steps.filter((st) => !st.done).length;
    return {
      lifecycle: steps,
      verdict: open > 0
        ? `Marked Complete — but ${open} step${open === 1 ? '' : 's'} below ${open === 1 ? 'is' : 'are'} still open (money may be uncollected or unreconciled)`
        : '',
    };
  }
  return {
    lifecycle: steps,
    verdict: firstMiss ? `In Progress because: ${firstMiss.label.toLowerCase()}${firstMiss.detail ? ` (${firstMiss.detail})` : ''}` : '',
  };
};

const useSalesBible = () => {
  const proposals = useProposals();
  const workOrders = useWorkOrders();
  const invoices = useInvoices();
  const users = useUsers();
  const commissions = useCommissions();

  const rows = useMemo<SalesBibleRow[]>(() => {
    // item 623 — VOID jobs are now surfaced (a "Voided" bucket at the bottom), so keep void
    // rows; only ARCHIVED (soft-deleted) rows are dropped.
    // item 714 — no archived rows; item 868b (Chad) — an UNSENT customer request isn't on the
    // sales board yet ("we don't use Pending until we send"); it appears once priced-and-sent.
    const props = (proposals.data?.index ?? []).filter(
      (p) => !isArchivedRow(p) && !((p as any).isCustomerSubmission && !(p as any).sentDate)
        && !(p as any).isContractOnly, // item 1180 — paper-only: not a job to track
    );
    const wos = workOrders.data?.index ?? [];
    const invs = invoices.data?.index ?? [];
    const comms = commissions.data?.index ?? [];

    const nameById = new Map((users.data?.index ?? []).map((u) => [u.id, u.name]));
    const woByProposal = new Map<string, typeof wos[number]>();
    wos.forEach((w) => { if (w.proposalId && !woByProposal.has(w.proposalId)) woByProposal.set(w.proposalId, w); });
    const proposalHasInvoice = new Set(invs.filter((i) => !i.isVoid && i.proposalId).map((i) => i.proposalId));
    // item 623 — the job's invoices, for the "accounting fully done" → Completed test
    const invsByProposal = new Map<string, typeof invs>();
    const invsByWo = new Map<string, typeof invs>();
    invs.forEach((i) => {
      if (i.proposalId) {
        const arr = invsByProposal.get(i.proposalId) ?? [];
        arr.push(i);
        invsByProposal.set(i.proposalId, arr);
      }
      if (i.workOrderId) {
        const arr = invsByWo.get(i.workOrderId) ?? [];
        arr.push(i);
        invsByWo.set(i.workOrderId, arr);
      }
    });

    // item 606 — group the job's ACCRUED/ASSIGNED commission rows (real commission entries,
    // not manual reimbursements/adjustments) by reason → the distinct earners' names.
    const earnersFor = (predicate: (c: typeof comms[number]) => boolean): CommissionEarnerGroup[] => {
      const byReason = new Map<string, string[]>();
      comms
        .filter((c) => c.entryType === CommissionEntryType.Commission && !!c.earnedByName && predicate(c))
        .forEach((c) => {
          const reason = commissionTypeLabel(c) || 'Commission';
          const names = byReason.get(reason) ?? [];
          if (!names.includes(c.earnedByName)) names.push(c.earnedByName);
          byReason.set(reason, names);
        });
      return Array.from(byReason.entries()).map(([reason, names]) => ({ reason, names }));
    };

    // item 607 — proposal-based jobs (unchanged behavior + the new columns)
    const proposalRows: SalesBibleRow[] = props.map((p) => {
      const wo = woByProposal.get(p.id);
      const finalBilled = wo?.status === WorkOrderStatus.InvoicesGenerated;
      const state = mapProposalState(p.status, !!p.isVoid, !!finalBilled, invsByProposal.get(p.id) ?? []);
      const completed = state === 'Completed';
      const contracted = p.status === ProposalStatus.Approved
        || p.status === ProposalStatus.ApprovedDeposited
        || p.status === ProposalStatus.Complete;
      const depositBilled = p.status === ProposalStatus.ApprovedDeposited
        || p.status === ProposalStatus.Complete
        || proposalHasInvoice.has(p.id);
      const lc = buildLifecycle(p.status, wo, invsByProposal.get(p.id) ?? [],
        p.status === ProposalStatus.Complete || state === 'Completed' || state === 'Declined' || state === 'Voided');
      return {
        id: p.id,
        kind: 'proposal',
        jobName: p.proposalTitle || p.proposalNumber || '(untitled)',
        projectName: p.projectName || '—', // item 1587
        state,
        bucket: STATE_BUCKET[state],
        completed,
        integrator: p.customerName ?? '—',
        customerStaffPm: p.customerProjectManagerName || '—', // item 612
        workOrderId: wo?.id, // item 1171
        customerId: p.customerId, // item 1172
        projectId: p.projectId || undefined, // item 1458
        customerPmId: p.customerProjectManagerId || undefined, // item 1458
        hasHandOff: !p.isVoid && contracted, // item 1460 — contracted = Approved/ApprovedDeposited/Complete
        ...billedInvoiceIds(invsByProposal.get(p.id) ?? [], !!finalBilled), // item 1458
        salesPerson: p.salesRepName ?? '—',
        // item 569 — the REAL lead assignment(s) from the approval wizard win; the WO's
        // ProjectManager is the legacy fallback. 'Not Assigned' only when neither exists.
        projectLead: (p.projectLeadNames ?? []).join(', ')
          || (wo?.projectManagerId && nameById.get(wo.projectManagerId))
          || 'Not Assigned',
        commissionEarners: earnersFor((c) => c.proposalId === p.id), // item 606
        contracted,
        depositBilled,
        finalBilled: !!finalBilled,
        proposalNumber: p.proposalNumber ?? '—',
        // item 611 — the customer's real PO (from the approval); item 1262 — falls back to
        // the client PO(s) captured on the job's invoices (the post-finalize 733 field)
        poNumber: poOf(p.poNumber, invsByProposal.get(p.id) ?? []),
        workOrderNumber: wo?.workOrderNumber ?? '', // item 609 — job code once a WO exists
        date: p.date ? p.date.slice(0, 10) : '',
        closedDate: p.lastModifiedDate ? p.lastModifiedDate.slice(0, 10) : '',
        // item 597 — the proposal's OWN platform is the Sales Bible source
        platform: p.platform || '',
        deploymentDate: p.deploymentDate ? p.deploymentDate.slice(0, 10) : '',
        illustrationsComplete: p.illustrationsComplete ?? '',
        projectLeadPayoutInput: p.projectLeadPayoutInput ?? '',
        notes: p.salesBibleNotes ?? '',
        lifecycle: lc.lifecycle, // item 1144
        verdict: lc.verdict,
        ...paymentFacts(invsByProposal.get(p.id) ?? []), // item 1258 — PAYMENT column
      };
    });

    // item 607 — standalone (proposal-less) work-order / service jobs: a sold, contracted
    // job in its own right. Excludes anything void/archived.
    const standaloneWoRows: SalesBibleRow[] = wos
      .filter((w) => !w.proposalId && !isArchivedRow(w)) // item 623 keep void; item 714 both archive fields
      // item 1260 (Chad: "this is a sales order — should not trigger a new job until invoice
      // finalized") — an SO is a QUOTE, not a job: it joins the board only once at least one
      // live invoice is FINALIZED (real billing exists). Work orders board immediately as before.
      .filter((w) => !w.isSalesOrder
        || (invsByWo.get(w.id) ?? []).some((i) => !i.isVoid && i.isFinalized))
      .map((w) => {
        const finalBilled = w.status === WorkOrderStatus.InvoicesGenerated;
        // item 623 — a standalone WO is contracted work: void → Voided; billed-through + fully
        // reconciled (accounting done) → Completed; otherwise still In Progress.
        let state: SalesState = 'In Progress';
        if (w.isVoid) state = 'Voided';
        // item 1258 — Completed at $0 left to bill (fully invoiced + every invoice finalized)
        else if (finalBilled && billedThrough(invsByWo.get(w.id) ?? [])) state = 'Completed';
        const lcWo = buildLifecycle(null, w, invsByWo.get(w.id) ?? [],
          state === 'Completed' || state === 'Voided');
        return {
          id: w.id,
          kind: 'workOrder',
          jobName: w.workOrderTitle || w.workOrderNumber || '(untitled)',
          projectName: w.projectName || '—', // item 1587
          state,
          bucket: STATE_BUCKET[state],
          completed: state === 'Completed',
          integrator: w.customerName ?? '—',
          customerStaffPm: w.customerProjectManagerName || '—', // item 612
          salesPerson: w.salesRepName ?? '—',
          // item 1210 — the REAL lead assignment(s) (WorkOrderLeads, the modal's store)
          // win; the WO's legacy ProjectManager is the fallback.
          projectLead: (w.leadNames ?? []).join(', ')
            || w.projectManagerName
            || (w.projectManagerId && nameById.get(w.projectManagerId))
            || 'Not Assigned',
          // item 606 — proposal-less deals carry the WO number on their commission rows
          commissionEarners: earnersFor((c) => !c.proposalId && !!c.workOrderNumber && c.workOrderNumber === w.workOrderNumber),
          contracted: true,
          depositBilled: !!w.hasLiveInvoice,
          finalBilled,
          proposalNumber: '—',
          // item 1262 — standalone WO jobs carry no proposal PO, but their invoices can
          poNumber: poOf(null, invsByWo.get(w.id) ?? []),
          workOrderNumber: w.workOrderNumber ?? '', // item 609 — the job code
          workOrderId: w.id, // item 1171
          customerId: w.customerId, // item 1172
          projectId: w.projectId || undefined, // item 1458
          customerPmId: w.customerProjectManagerId || undefined, // item 1458
          hasHandOff: false, // item 1460 — hand-offs are proposal documents
          ...billedInvoiceIds(invsByWo.get(w.id) ?? [], finalBilled), // item 1458
          date: w.date ? w.date.slice(0, 10) : '',
          closedDate: w.lastModifiedDate ? w.lastModifiedDate.slice(0, 10) : '',
          platform: w.platform || '',
          // item 1210 — the WO carries its OWN Sales Bible manual columns now (the
          // update endpoint accepts a WO id); payout-input stays proposal-only (legacy).
          deploymentDate: w.deploymentDate ? w.deploymentDate.slice(0, 10) : '',
          illustrationsComplete: w.illustrationsComplete ?? '',
          projectLeadPayoutInput: '',
          notes: w.salesBibleNotes ?? '',
          lifecycle: lcWo.lifecycle, // item 1144
          verdict: lcWo.verdict,
          ...paymentFacts(invsByWo.get(w.id) ?? []), // item 1258 — PAYMENT column
        };
      });

    return [...proposalRows, ...standaloneWoRows];
  }, [proposals.data, workOrders.data, invoices.data, users.data, commissions.data]);

  return {
    rows,
    // item 1082 (Chad: "impersonating team still can't load sales bible") — invoices +
    // commissions are MONEY feeds that Team is rightly denied (item 34); a 403 on either
    // must not kill the whole board. Loading waits on them only until they settle
    // (success OR error); the error state keys on the feeds every bible viewer can read.
    // For Team the billing/commission cells simply render empty.
    isLoading: proposals.isLoading || workOrders.isLoading || users.isLoading
      || (invoices.isLoading && !invoices.isError)
      || (commissions.isLoading && !commissions.isError),
    isError: proposals.isError || workOrders.isError || users.isError,
  };
};

export default useSalesBible;
